import type { ZudokuConfig } from "zudoku";
import { createElement } from "react";

const PT_BR_TRANSLATIONS: Array<[string, string]> = [
  ["Last modified on", "Última modificação em"],
  ["On this page", "Nesta página"],
  ["Information", "Visão Geral"],
];

const ptBrTranslateScript = `
(function () {
  if (window.__zudokuPtBr) return;
  window.__zudokuPtBr = true;
  var P = ${JSON.stringify(PT_BR_TRANSLATIONS)};
  function tx(v) {
    for (var i = 0; i < P.length; i++) {
      if (v.indexOf(P[i][0]) !== -1) v = v.split(P[i][0]).join(P[i][1]);
    }
    return v;
  }
  function walk(node) {
    if (!node) return;
    if (node.nodeType === 3) {
      var nv = tx(node.nodeValue);
      if (nv !== node.nodeValue) node.nodeValue = nv;
      return;
    }
    if (node.nodeType !== 1) return;
    var tag = node.nodeName;
    if (tag === "SCRIPT" || tag === "STYLE" || tag === "NOSCRIPT") return;
    for (var c = node.firstChild; c; c = c.nextSibling) walk(c);
  }
  function start() {
    walk(document.body);
    var mo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var m = muts[i];
        if (m.addedNodes && m.addedNodes.length) {
          for (var j = 0; j < m.addedNodes.length; j++) walk(m.addedNodes[j]);
        }
        if (m.type === "characterData") walk(m.target);
      }
    });
    mo.observe(document.body, { childList: true, subtree: true, characterData: true });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();
`;

// IMPORTANTE: @zudoku/react-helmet-async injeta o conteúdo de <script> apenas
// quando passado como children (string). Usar `dangerouslySetInnerHTML` aqui
// resulta em <script> vazio em runtime — o JS nunca executa. Mantenha
// `createElement("script", null, scriptString)`.
const ptBrPlugin = {
  getHead: () => createElement("script", null, ptBrTranslateScript),
};

// Logo Brasil NFe aponta para www.brasilnfe.com.br/ (mesmo origin da SPA Zudoku
// em produção). Sem este patch, o React Router intercepta o clique e tenta
// navegar internamente para "/", que não existe no app Zudoku → 404.
// Capturamos o clique em fase de captura (antes do React) e forçamos full page.
const logoExternalScript = `
(function () {
  if (window.__bnfeLogoPatch) return;
  window.__bnfeLogoPatch = true;
  function patch() {
    var img = document.querySelector('img[alt="Brasil NFe"]');
    if (!img) return false;
    var a = img.closest('a');
    if (!a || a.dataset.bnfeLogoPatched) return true;
    a.dataset.bnfeLogoPatched = '1';
    a.addEventListener('click', function (e) {
      var href = a.getAttribute('href');
      if (!href) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      window.location.assign(href);
    }, true);
    return true;
  }
  function start() {
    if (patch()) return;
    var obs = new MutationObserver(function () { if (patch()) obs.disconnect(); });
    obs.observe(document.documentElement, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
`;

const logoExternalPlugin = {
  getHead: () => createElement("script", null, logoExternalScript),
};

// Links same-origin que apontam para páginas estáticas FORA do SPA Zudoku
// (ex.: /aliquotas-icms/, /products/*, /legal/*, /privacidade) caem em 404 do
// Zudoku porque o React Router intercepta o clique. Capturamos em fase de
// captura, antes do React, e forçamos full page nav. Pathnames sob os
// prefixos do SPA continuam navegando internamente.
const siteFullNavScript = `
(function () {
  if (window.__bnfeSiteFullNav) return;
  window.__bnfeSiteFullNav = true;
  var SPA_PREFIXES = ['/docs', '/ai-docs', '/agente-docs', '/api', '/api-chat-ia', '/conceitos-fiscais', '/mcp', '/ai-chat', '/webhooks',
    '/rate-limit', '/datetime', '/percentage', '/files', '/security',
    '/autentication', '/support'];
  function isSpaPath(p) {
    if (!p) return false;
    for (var i = 0; i < SPA_PREFIXES.length; i++) {
      if (p === SPA_PREFIXES[i] || p.indexOf(SPA_PREFIXES[i] + '/') === 0) return true;
    }
    return false;
  }
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target && e.target.closest && e.target.closest('a[href]');
    if (!a) return;
    if (a.target && a.target !== '' && a.target !== '_self') return;
    var href = a.getAttribute('href');
    if (!href || href.charAt(0) === '#') return;
    var url;
    try { url = new URL(href, window.location.href); } catch (_) { return; }
    if (url.origin !== window.location.origin) return;
    if (isSpaPath(url.pathname)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    window.location.assign(url.href);
  }, true);
})();
`;

const siteFullNavPlugin = {
  getHead: () => createElement("script", null, siteFullNavScript),
};

// Mesmo script de medição das páginas estáticas (banner LGPD, Google Ads, GA4,
// origem da visita e link de cadastro). Manter o ?v= igual ao de partials/lazy-gtm.html.
const measurementScript = `
(function () {
  if (window.__bnfeMedicaoLoader) return;
  window.__bnfeMedicaoLoader = true;
  var s = document.createElement('script');
  s.src = '/scripts/lazy-gtm.js?v=5';
  s.defer = true;
  document.head.appendChild(s);
})();
`;

const measurementPlugin = {
  getHead: () => createElement("script", null, measurementScript),
};

// Espelha o generateSchemaExample do Zudoku (não exportado pelo pacote). Ele só
// gera o exemplo a partir do schema quando a operação não declara `examples`;
// aqui o exemplo gerado volta como primeira opção ao lado dos cenários do OpenAPI.
const schemaExample = (schema: any, name?: string): any => {
  if (!schema || typeof schema !== "object") return null;
  if (schema.example !== undefined) return schema.example;
  if (schema.const !== undefined) return schema.const;
  if (schema.default !== undefined) return schema.default;
  if (Array.isArray(schema.examples) && schema.examples.length > 0) {
    return schema.examples[0];
  }
  const type = Array.isArray(schema.type)
    ? (schema.type.find((t: string) => t !== "null") ?? schema.type[0])
    : schema.type;
  if (type === "object" && schema.properties) {
    return Object.fromEntries(
      Object.entries(schema.properties).map(([key, prop]) => [
        key,
        schemaExample(prop, key),
      ]),
    );
  }
  if (type === "array") {
    return schema.items ? [schemaExample(schema.items)] : [];
  }
  const byFormat: Record<string, string> = {
    "date-time": "2024-08-25T15:00:00Z",
    date: "2024-08-25",
    time: "15:00:00",
    email: "test@example.com",
    uri: "https://www.example.com/path/to/resource",
    uuid: "00000000-0000-0000-0000-000000000000",
  };
  if (byFormat[schema.format]) return byFormat[schema.format];
  if (schema.enum) return schema.enum[0];
  const variant = schema.oneOf ?? schema.anyOf;
  if (variant?.length) {
    return schemaExample(
      variant.find((s: any) => s.type !== "null") ?? variant[0],
    );
  }
  if (type === "string") return name || "string";
  if (type === "number" || type === "integer") return 0;
  if (type === "boolean") return true;
  if (type === "null") return null;
  return {};
};

const config: ZudokuConfig = {
  plugins: [ptBrPlugin, logoExternalPlugin, siteFullNavPlugin, measurementPlugin],
  site: {
    showPoweredBy: false,
    logo: {
      src: { light: "/logo2.webp", dark: "/logo1.webp" },
      alt: "Brasil NFe",
      width: "200px",
      href: "https://www.brasilnfe.com.br/",
    },
    footer: {
      copyright: `Copyright © 2019–${new Date().getFullYear()} BRASIL NFE LTDA · CNPJ 39.658.743/0001-99`,
      columns: [
        {
          title: "Produto",
          links: [
            { label: "Documentação", href: "/docs" },
            { label: "Referência da API", href: "/api" },
            { label: "Preços", href: "https://www.brasilnfe.com.br/#precos" },
            { label: "Painel", href: "https://api.brasilnfe.com.br" },
          ],
        },
        {
          title: "Empresa",
          links: [
            { label: "Site", href: "https://www.brasilnfe.com.br/" },
            { label: "Contato", href: "https://www.brasilnfe.com.br/#contato" },
            { label: "WhatsApp", href: "https://wa.me/+5531971685947" },
            { label: "LinkedIn", href: "https://www.linkedin.com/company/brasil-nfe/about/" },
          ],
        },
        {
          title: "Legal",
          links: [
            { label: "Privacidade", href: "https://www.brasilnfe.com.br/legal/privacidade" },
            { label: "Termos de Uso", href: "https://www.brasilnfe.com.br/legal/termos" },
            { label: "LGPD · Direitos", href: "https://www.brasilnfe.com.br/legal/lgpd" },
            { label: "Cookies", href: "https://www.brasilnfe.com.br/legal/cookies" },
          ],
        },
      ],
    },
  },
  metadata: {
    title: "%s | Brasil NFe",
    defaultTitle: "Documentação da API fiscal | Brasil NFe",
    favicon: "/favicon.ico",
  },
  canonicalUrlOrigin: "https://www.brasilnfe.com.br",
  sitemap: {
    siteUrl: "https://www.brasilnfe.com.br",
    outDir: ".sitemap-zudoku",
    changefreq: "weekly",
  },
  navigation: [
    {
      type: "category",
      label: "Documentação",
      items: [
        {
          type: "category",
          label: "Primeiros Passos",
          icon: "sparkles",
          items: [
            "/docs",
            "/support",
            "/autentication",
            "/security",
            "/rate-limit",
            "/webhooks",
            "/datetime",
            "/percentage",
            "/files",
            {
              type: "link",
              icon: "folder-cog",
              badge: {
                label: "New",
                color: "purple",
              },
              label: "Referência da API",
              to: "/api",
            },
          ],
        },
        {
          type: "link",
          icon: "sparkles",
          badge: {
            label: "New",
            color: "purple",
          },
          label: "Inteligência Artificial",
          to: "/ai-docs",
        },
        {
          type: "link",
          icon: "monitor",
          badge: {
            label: "New",
            color: "purple",
          },
          label: "Brasil NFe Agente",
          to: "/agente-docs/visao-geral",
        },
        {
          type: "category",
          label: "SDK Links",
          collapsible: false,
          icon: "link",
          items: [
            {
              type: "link",
              icon: "book",
              label: ".NET",
              to: "https://www.nuget.org/packages/BrasilNFe/",
            },
            {
              type: "link",
              icon: "book",
              label: "PHP",
              to: "https://packagist.org/packages/brasilnfe/brasilnfe-php-sdk",
            },
            {
              type: "link",
              icon: "book",
              label: "Node.js",
              to: "https://www.npmjs.com/package/brasilnfe",
            },
            {
              type: "link",
              icon: "book",
              label: "Python",
              to: "https://pypi.org/project/brasilnfe/",
            },
          ],
        },
      ],
    },
    {
      type: "link",
      to: "/api",
      label: "Referência da API",
    },
    {
      type: "category",
      label: "Inteligência Artificial",
      items: [
        "/ai-docs",
        "/mcp",
        "/ai-chat",
        {
          type: "link",
          icon: "folder-cog",
          badge: {
            label: "New",
            color: "purple",
          },
          label: "Referência da API (Chat)",
          to: "/api-chat-ia",
        },
      ],
    },
    {
      type: "category",
      label: "Brasil NFe Agente",
      items: [
        "/agente-docs/visao-geral",
        "/agente-docs/instalacao",
        "/agente-docs/pareamento-e-certificado",
        "/agente-docs/nfce-offline",
        "/agente-docs/cli-e-console",
        "/agente-docs/seguranca",
        {
          type: "link",
          icon: "download",
          label: "Baixar o Agente",
          to: "https://www.brasilnfe.com.br/agente/",
        },
      ],
    },
    {
      type: "category",
      label: "Conceitos Fiscais",
      items: [
        {
          type: "category",
          label: "ICMS",
          icon: "scale",
          collapsible: true,
          items: [
            "/conceitos-fiscais/difal",
            "/conceitos-fiscais/icms-st-cest",
            "/conceitos-fiscais/partilha-ec87",
            "/conceitos-fiscais/fcp",
            "/conceitos-fiscais/gnre",
            {
              type: "link",
              icon: "table",
              label: "Tabela de Alíquotas ICMS",
              to: "https://www.brasilnfe.com.br/aliquotas-icms/",
            },
          ],
        },
        {
          type: "category",
          label: "Outros Tributos",
          icon: "coins",
          collapsible: true,
          items: [
            "/conceitos-fiscais/ipi-cst",
            "/conceitos-fiscais/pis-cofins",
          ],
        },
        {
          type: "category",
          label: "Regimes & Operações",
          icon: "settings",
          collapsible: true,
          items: [
            "/conceitos-fiscais/regimes-tributarios",
            "/conceitos-fiscais/csosn-vs-cst",
            "/conceitos-fiscais/cfop-derivacoes",
            "/conceitos-fiscais/numeracao-e-series",
            "/conceitos-fiscais/defaults-automaticos",
          ],
        },
        {
          type: "category",
          label: "NFS-e",
          icon: "building-2",
          collapsible: true,
          items: [
            "/conceitos-fiscais/retencoes-nfse",
            "/conceitos-fiscais/nfse-por-municipio",
          ],
        },
        {
          type: "category",
          label: "SEFAZ & Infraestrutura",
          icon: "shield-check",
          collapsible: true,
          items: [
            "/conceitos-fiscais/contingencia",
            "/conceitos-fiscais/certificado-digital",
            {
              type: "category",
              label: "Rejeições SEFAZ",
              icon: "alert-triangle",
              collapsible: true,
              items: [
                "/conceitos-fiscais/rejeicoes/index",
                "/conceitos-fiscais/rejeicoes/infraestrutura",
                "/conceitos-fiscais/rejeicoes/autoria-certificado",
                "/conceitos-fiscais/rejeicoes/cadastro",
                "/conceitos-fiscais/rejeicoes/schema-xml",
                "/conceitos-fiscais/rejeicoes/chave-numeracao",
                "/conceitos-fiscais/rejeicoes/uf-municipio-ambiente",
                "/conceitos-fiscais/rejeicoes/datas-prazos",
                "/conceitos-fiscais/rejeicoes/cfop-operacao",
                "/conceitos-fiscais/rejeicoes/icms-tributacao",
                "/conceitos-fiscais/rejeicoes/outros-tributos",
                "/conceitos-fiscais/rejeicoes/totalizadores",
                "/conceitos-fiscais/rejeicoes/produto-identificacao",
                "/conceitos-fiscais/rejeicoes/pagamentos",
                "/conceitos-fiscais/rejeicoes/transporte-combustivel",
                "/conceitos-fiscais/rejeicoes/eventos",
                "/conceitos-fiscais/rejeicoes/regimes-especiais",
              ],
            },
          ],
        },
      ],
    },
  ],
  // redirects: [{ from: "/", to: "/docs" }],
  apis: [
    {
      type: "file",
      input: "./apis/openapi.json",
      path: "/api",
      options: {
        transformExamples: ({ type, content }) =>
          type !== "request"
            ? content
            : content.map((c) =>
                c.examples?.length && c.schema
                  ? {
                      ...c,
                      examples: [
                        {
                          name: "completo",
                          summary: "Exemplo completo (todos os campos)",
                          value: schemaExample(c.schema),
                        },
                        ...c.examples,
                      ],
                    }
                  : c,
              ),
      },
    },
    {
      type: "file",
      input: "./apis/ai-chat-openapi.json",
      path: "/api-chat-ia",
    },
  ],
  docs: {
    files: "pages/**/*.{md,mdx}",
    publishMarkdown: true,
    llms: {
      llmsTxt: true,
      llmsTxtFull: true,
    },
  },
};

export default config;
